/**
 * The rules that decide what plays next.
 *
 * They live apart from the player so they can be reasoned about — and tested —
 * without a YouTube iframe. MusicPlayerContext owns the iframes, the timers and
 * the React state; everything here is a pure function of what it observed.
 */

/** YouTube IFrame API player states. */
export const PLAYER_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
};

/**
 * Why a video refused to play, in the words of the IFrame API.
 *
 * 101 and 150 are the same refusal reported twice: the uploader disallowed
 * embedding. For a catalogue built out of YouTube search results they are by
 * far the most common, which is why an unplayable track has to be an ordinary
 * event rather than an edge case.
 */
export const YT_ERROR = {
  2: "bad-id",
  5: "html5",
  100: "gone",
  101: "embed-blocked",
  150: "embed-blocked",
};

/**
 * Whether an error means this video will never play here.
 *
 * 5 is the HTML5 player itself failing, which is often transient — a reload of
 * the same id frequently works — so it is worth another attempt before the
 * track is written off. Every other code is final: the id is malformed, the
 * video is gone, or its owner does not allow embedding.
 */
export function isPermanentError(code) {
  return code === 2 || code === 100 || code === 101 || code === 150;
}

/**
 * Where the queue goes after `index`, or null when there is nowhere to go.
 *
 * Repeat-one is deliberately not handled here: repeating is not advancing, and
 * the caller has to restart the same video rather than change index.
 */
export function pickNextIndex({ index, size, shuffle = false, repeat = "off", random = Math.random }) {
  if (!size || size < 1) return null;
  if (index < 0) return 0;
  if (shuffle && size > 1) {
    let pick = index;
    // A retry loop rather than arithmetic on the result, so that every other
    // track stays equally likely instead of the neighbour being favoured.
    for (let guard = 0; pick === index && guard < 24; guard += 1) {
      pick = Math.floor(random() * size);
    }
    if (pick === index || pick < 0 || pick >= size) pick = (index + 1) % size;
    return pick;
  }
  if (index < size - 1) return index + 1;
  return repeat === "all" ? 0 : null;
}

/**
 * The next index holding a track that can actually be played.
 *
 * A track whose video has already refused to load would stall the queue again
 * the moment it came round, so those are stepped over. Giving up after a full
 * lap stops a queue of entirely dead videos from spinning forever.
 */
export function pickNextPlayable({ index, size, shuffle = false, repeat = "off", isPlayable = () => true, random = Math.random }) {
  let cursor = index;
  const seen = new Set([index]);
  for (let hop = 0; hop < size; hop += 1) {
    const candidate = pickNextIndex({ index: cursor, size, shuffle, repeat, random });
    if (candidate === null) return null;
    if (isPlayable(candidate)) return candidate;
    if (seen.has(candidate)) return null;
    seen.add(candidate);
    cursor = candidate;
  }
  return null;
}

/** How patient the watchdog is, in milliseconds unless the name says seconds. */
export const WATCHDOG = {
  /** How long a track gets to start before the player is asked again. */
  startGraceMs: 9000,
  /** How long to wait between escalations, once something has been tried.
   *  The first wait is generous because a slow line is not a fault; the ones
   *  after it are short, because by then something is demonstrably wrong and
   *  the listener is sitting in silence. */
  nudgeWaitMs: 3500,
  /** The same, once it has played before and something paused it for us. */
  pausedGraceMs: 2500,
  /** How long it may sit visibly buffering, which is fair on a slow line. */
  bufferGraceMs: 28000,
  /** How long a playing track may report the same position before it is stuck. */
  frozenMs: 12000,
  /** The same, but within touching distance of the end. */
  endFrozenMs: 2500,
  /** How close to the end counts as the end. */
  endWindowS: 1.25,
  /** Attempts to talk it round before something heavier is tried. */
  maxNudges: 2,
};

/**
 * What to do about the state the player is sitting in.
 *
 * The IFrame player has several ways of going quiet that are not an error and
 * not an ending: it can stay UNSTARTED after a load, buffer without end, be
 * paused out from under us by the browser, or run to the last second of a
 * video and never emit ENDED. Each of those looks identical to the listener —
 * the music stopped and the queue did not move on — so all of them have to be
 * caught here and turned back into progress.
 *
 * Returns one of:
 *   "wait"     nothing is wrong yet
 *   "nudge"    ask it to play again
 *   "reload"   load the same video from scratch
 *   "advance"  give up on this track
 *   "ended"    it finished without saying so
 */
export function stallVerdict(
  {
    state,
    position = 0,
    duration = 0,
    sinceProgressMs = 0,
    sinceAttemptMs = 0,
    hasPlayed = false,
    nudges = 0,
    reloaded = false,
    wantsPlay = true,
    visible = true,
  },
  cfg = WATCHDOG,
) {
  // A deliberate pause is not a stall. Nothing below may override the
  // listener's own decision to stop the music.
  if (!wantsPlay) return "wait";

  const S = PLAYER_STATE;
  if (state === S.ENDED) return "ended";

  // Ran to the end and went quiet without an ENDED event.
  if (
    hasPlayed &&
    duration > 0 &&
    position > 0 &&
    duration - position <= cfg.endWindowS &&
    sinceProgressMs >= cfg.endFrozenMs
  ) {
    return "ended";
  }

  if (state === S.PLAYING) {
    // Claims to be playing, but the clock has not moved.
    const frozenFor = nudges > 0 ? cfg.nudgeWaitMs : cfg.frozenMs;
    if (hasPlayed && sinceProgressMs >= frozenFor) {
      if (nudges < cfg.maxNudges) return "nudge";
      return reloaded ? "advance" : "reload";
    }
    return "wait";
  }

  if (state === S.BUFFERING) {
    if (sinceAttemptMs < cfg.bufferGraceMs) return "wait";
    return reloaded ? "advance" : "reload";
  }

  if (state === S.PAUSED || state === S.CUED || state === S.UNSTARTED) {
    // YouTube pauses an embedded player whenever a mobile browser backgrounds
    // the page, and that is their rule rather than a fault of ours. Skipping
    // the track for it would silently eat the queue in a pocket.
    if (!visible) return "wait";
    const grace = nudges > 0
      ? cfg.nudgeWaitMs
      : hasPlayed ? cfg.pausedGraceMs : cfg.startGraceMs;
    if (sinceProgressMs < grace) return "wait";
    if (nudges < cfg.maxNudges) return "nudge";
    return reloaded ? "advance" : "reload";
  }

  return "wait";
}

/**
 * Where the playing index lands after a track is taken out of the queue.
 *
 * Removing something earlier in the list shifts everything after it down, and
 * the index has to come with it — otherwise pulling a played track off the top
 * jumps the music to a different song, which is what used to happen.
 */
export function indexAfterRemoval(index, removed) {
  if (removed < index) return index - 1;
  return index;
}

/** The same, for a drag that reorders the queue. */
export function indexAfterMove(index, from, to) {
  if (from === index) return to;
  if (from < index && to >= index) return index - 1;
  if (from > index && to <= index) return index + 1;
  return index;
}
